import {useState} from 'react';
import {I18n} from 'aws-amplify/utils';
import {
    Alert,
    Button,
    Drawer,
    DrawerProps,
    Group,
    ScrollArea,
    Stack,
    Text,
} from '@mantine/core';

import {GrantChecklist} from '../components/GrantChecklist';
import {useDeclareProviderCapabilitiesMutation} from '../hooks/useProviderConnectionsQuery';
import {RoleGrantSelection} from '../services/rolePolicyBuilder';
import {
    buildDeclareInput,
    declaredCertificateGrant,
    declaredDnsGrant,
    hasCertificateDeclaration,
    ProviderConnection,
} from '../types';
import {RoleGrantKey} from '../services/rolePolicyBuilder';
import {
    showErrorNotifications,
    showSuccessNotification,
} from '../../../components/ServiceNotifications';
import {productCopy} from '../../../config/productCopy';

/**
 * FNP-1400 — CORRECT WHAT THIS CONNECTION DECLARES.
 *
 * A declaration is the tenant's own statement about the IAM grants they made in
 * their own AWS account. Until now it could be made once, at connect time, and
 * never corrected (FNP-1376). A mis-ticked box, a role widened later, a role
 * tightened later — none could be brought back into line, so the product went
 * on offering buttons it knew might fail at the provider.
 *
 * ⚠️ THE SAME CHECKBOXES AS THE CONNECT DRAWER, ON PURPOSE. FNP-1363
 * established that the tick and the stored declaration are one act, so a
 * connection cannot claim a permission its role was never granted. A correction
 * screen with its own differently-worded controls would be a second place to
 * describe permissions, and the two would drift. `GrantChecklist` is the one
 * copy.
 *
 * ⚠️ FNP-1398 — IT ASKS ABOUT CERTIFICATES TOO, WHEN IT CAN ANSWER HONESTLY.
 * The two certificate ticks were hidden here from the start, because nothing
 * typed `certificateCapabilities` and a tick seeded from an invented false
 * would revoke a real grant on submit. The field is typed now, so the drawer
 * seeds them from the stored declaration and sends the block — but only when
 * the block actually arrived on the response. `certificateKnown` gates the
 * seed, the questions and the send together; see `buildDeclareInput`.
 *
 * ⚠️ NO POLICY TO PASTE HERE. The connect drawer shows the permission policy,
 * the trust policy and the external id because a role is being CREATED. The
 * role already exists by the time a tenant reaches this drawer; they are
 * telling Flexi what they granted it, not building it. Showing a policy would
 * imply they must go and re-paste something, which is the opposite of the
 * intent.
 *
 * ⚠️ AND NO CREDENTIAL. The mutation's input has no credential field at all, so
 * this drawer structurally cannot repoint the connection at a different role —
 * the trap FNP-1265 removed from re-validate, where a required-but-blank ARN
 * field meant a typo silently changed which role was assumed.
 */
/**
 * The questions this drawer can ask when the certificate declaration did NOT
 * arrive on the response. `readDns` is included because the checklist renders
 * it as a fixed fact rather than a control.
 */
const DNS_GRANTS: readonly RoleGrantKey[] = [
    'readDns',
    'writeRecords',
    'createZones',
    'deleteZones',
];

/**
 * FNP-1398 — and the full set, once the stored certificate declaration is
 * readable and can seed the two extra ticks honestly.
 */
const DNS_AND_CERTIFICATE_GRANTS: readonly RoleGrantKey[] = [
    ...DNS_GRANTS,
    'requestCertificates',
    'deleteCertificates',
];

export const DeclareCapabilitiesDrawer = (
    {
        connection,
        opened,
        onClose,
        ...rest
    }: DrawerProps & {
        connection: ProviderConnection,
    },
) => {

    const declareMutation = useDeclareProviderCapabilitiesMutation();

    /**
     * ⚠️ SEEDED FROM THE CURRENT DECLARATION, NEVER FROM AN EMPTY FORM.
     *
     * This is the single most important line in the drawer. A tenant correcting
     * ONE grant must not have to re-tick the other five to keep them: an empty
     * form submitted whole would read as a revocation of everything untouched,
     * and the mutation would apply it — silently, successfully, and wrongly.
     *
     * `declaredDnsGrant` is the fail-closed reader: an absent block, an absent
     * field, a null and an explicit false all resolve to NOT granted, so a
     * connection that predates the split seeds as read-only rather than
     * inventing permissions from undefined.
     *
     * `readDns` is always true — it is the minimum needed to validate at all,
     * and the checklist renders it as a fixed fact rather than a control.
     */
    /**
     * FNP-1398 — CAN THIS RESPONSE'S CERTIFICATE DECLARATION BE READ AT ALL?
     *
     * ⚠️ COMPUTED ONCE AND USED THREE TIMES — the seed, the questions asked,
     * and the block sent — because those three MUST agree. A drawer that asked
     * a question it could not seed would render an invented tick, and
     * submitting that tick would revoke a real grant.
     */
    const certificateKnown = hasCertificateDeclaration(connection);

    const [selection, setSelection] = useState<RoleGrantSelection>({
        readDns: true,
        writeRecords: declaredDnsGrant(connection, 'canWriteRecords'),
        createZones: declaredDnsGrant(connection, 'canCreateZones'),
        deleteZones: declaredDnsGrant(connection, 'canDeleteZones'),
        // ⚠️ FNP-1398 — SEEDED FROM THE STORED DECLARATION, exactly like the
        // DNS grants above, now that the field is typed and readable. When the
        // block did NOT arrive these seed false and are neither shown nor sent
        // — `certificateKnown` gates all three — so the false is inert rather
        // than a revocation waiting for a submit.
        requestCertificates: declaredCertificateGrant(connection, 'canRequest'),
        deleteCertificates: declaredCertificateGrant(connection, 'canDelete'),
    });

    const handleSubmit = async () => {
        try {
            await declareMutation.mutateAsync(
                // ⚠️ THE READABILITY GATE IS PASSED EXPLICITLY. `buildDeclareInput`
                // sends the certificate block only when this is true, so a
                // connection whose block never arrived keeps its stored ACM
                // grants untouched instead of having them revoked by a
                // correction that only meant to change DNS.
                buildDeclareInput(connection.id, selection, certificateKnown),
            );

            showSuccessNotification(I18n.get('Declaration updated.'));
            onClose();

        } catch (e) {
            // The call failed. The drawer stays open with what the tenant
            // ticked, so a retry does not start from the stored values again.
            //
            // ⚠️ NOT RETHROWN, UNLIKE THE RENAME AND CONNECT DRAWERS. Those
            // hand `handleSubmit` to Formik, which catches the rethrow and uses
            // it to keep the form in a submitting-failed state. This button
            // calls the handler directly from `onClick`, so a rethrow reaches
            // nobody: it becomes an unhandled promise rejection that fails the
            // whole test run while every assertion still passes. Showing the
            // error and staying open is the entire contract here.
            showErrorNotifications(e);
        }
    };

    return (
        <Drawer
            transitionProps={{
                transition: 'slide-up',
                duration: 750,
                timingFunction: 'ease',
            }}
            position={'right'}
            offset={10}
            opened={opened}
            onClose={onClose}
            title={I18n.get('Edit declared permissions')}
            padding={'xl'}
            size={'xl'}
            scrollAreaComponent={ScrollArea.Autosize}
            {...rest}
        >
            <Stack gap={'md'}>

                {/* ⚠️ SAYS WHAT THIS DOES *NOT* DO, because the neighbouring
                    buttons do those things. A tenant arriving here from
                    Re-validate needs to know this is not a re-check, and a
                    tenant worried about their credential needs to know nothing
                    is being changed at the provider.

                    ⚠️ THE "NOT A RE-CHECK" SENTENCE IS UNCONDITIONAL. It used to
                    sit inside the certificateKnown branch, so a connection with
                    no ACM grants on file lost the one line that stops a tenant
                    reading this drawer as a re-validation. The certificate note
                    is the part that varies; the rest is true either way. */}
                <Alert color={'gray'} variant={'light'}>
                    <Text size={'sm'}>
                        {productCopy('Records what you granted in your AWS account. Does not re-check the connection — use Re-validate for that.')}
                        {!certificateKnown && ' '}
                        {!certificateKnown
                            && productCopy('No certificate permissions on file, so they are left unchanged.')}
                    </Text>
                </Alert>

                <GrantChecklist
                    selection={selection}
                    onChange={setSelection}
                    intro={certificateKnown
                        ? 'Tick what this role is actually permitted to do. {product} offers only the actions you declare here, so removing a tick hides the controls that depend on it.'
                        : 'Tick what this role is actually permitted to do in your DNS. {product} offers only the actions you declare here, so removing a tick hides the controls that depend on it.'}
                    // ⚠️ FNP-1398 — THE CERTIFICATE QUESTIONS APPEAR ONLY WHEN
                    // THEIR CURRENT ANSWER WAS READABLE, and the same flag also
                    // decides whether `buildDeclareInput` sends the block.
                    // Asking a question whose answer cannot be read would
                    // render an invented tick; submitting it would revoke a
                    // real grant. Showing and sending must never disagree.
                    only={certificateKnown ? DNS_AND_CERTIFICATE_GRANTS : DNS_GRANTS}
                    // ⚠️ WITHHELD ON PURPOSE. "Start read-only" is reassuring
                    // when deciding what to grant; on a correction it is a
                    // one-click revocation of everything the tenant currently
                    // has, under a heading about editing.
                    showReadOnlyShortcut={false}
                />

                <Group justify={'flex-end'} mt={'md'}>
                    <Button
                        variant={'subtle'}
                        onClick={onClose}
                        disabled={declareMutation.isPending}
                    >
                        {I18n.get('Cancel')}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        loading={declareMutation.isPending}
                        data-testid={'declare-submit'}
                    >
                        {I18n.get('Save declaration')}
                    </Button>
                </Group>
            </Stack>
        </Drawer>
    );
};
